import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Info,
  ClipboardList,
  Eye,
  DollarSign,
  Upload,
  ShieldCheck,
  ShieldX,
  ShieldAlert,
  FileText,
  Receipt,
  AlertCircle,
} from 'lucide-react';
import type { TInsuranceNotificationType, IInsuranceNotification } from '@/@types/notification';
import type {
  TNotificationCategory,
  TDateGroup,
  INotificationDateGroup,
  TNotificationStatusColor,
  INotificationStatusVisual,
} from '@/@types/notificationPanel';

// ============================================================================
// Category Mapping
// ============================================================================

const TYPE_TO_CATEGORY: Record<TInsuranceNotificationType, TNotificationCategory> = {
  claim_submitted: 'claims',
  claim_under_review: 'claims',
  claim_additional_info_required: 'claims',
  claim_approved: 'claims',
  claim_partially_approved: 'claims',
  claim_rejected: 'claims',
  claim_paid: 'claims',
  claim_partially_paid: 'claims',
  claim_docs_uploaded: 'claims',
  policy_expiring: 'policies',
  policy_renewed: 'policies',
  policy_cancelled: 'policies',
  policy_submitted_for_review: 'policies',
  policy_approved: 'policies',
  policy_rejected: 'policies',
  preauth_requested: 'preauth',
  preauth_approved: 'preauth',
  preauth_denied: 'preauth',
  preauth_expiring: 'preauth',
  preauth_submitted: 'preauth',
  preauth_under_review: 'preauth',
  invoice_created: 'invoices',
  receipt_uploaded: 'invoices',
  ai_status_update: 'claims',
};

export function getNotificationCategory(type: TInsuranceNotificationType): TNotificationCategory {
  return TYPE_TO_CATEGORY[type];
}

// ============================================================================
// Date Grouping
// ============================================================================

function getDateGroup(timestamp: number): TDateGroup {
  const now = new Date();

  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = todayStart - 86400000;
  const weekStart = todayStart - 6 * 86400000;

  if (timestamp >= todayStart) return 'Today';
  if (timestamp >= yesterdayStart) return 'Yesterday';
  if (timestamp >= weekStart) return 'This Week';
  return 'Older';
}

export function groupNotificationsByDate(
  notifications: IInsuranceNotification[]
): INotificationDateGroup[] {
  const groups: Map<TDateGroup, IInsuranceNotification[]> = new Map();
  const order: TDateGroup[] = ['Today', 'Yesterday', 'This Week', 'Older'];

  for (const notification of notifications) {
    const group = getDateGroup(notification.createdAt);
    const existing = groups.get(group);
    if (existing) {
      existing.push(notification);
    } else {
      groups.set(group, [notification]);
    }
  }

  return order
    .filter(label => groups.has(label))
    .map(label => ({
      label,
      notifications: groups.get(label) ?? [],
    }));
}

// ============================================================================
// Time Formatting
// ============================================================================

export function formatTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

// ============================================================================
// Status Color Resolution
// ============================================================================

const STATUS_VISUALS: Record<TNotificationStatusColor, INotificationStatusVisual> = {
  green: {
    color: 'green',
    borderClass: 'bg-green-500',
    dotClass: 'bg-green-500',
    iconClass: 'text-green-500',
  },
  amber: {
    color: 'amber',
    borderClass: 'bg-amber-500',
    dotClass: 'bg-amber-500',
    iconClass: 'text-amber-500',
  },
  red: {
    color: 'red',
    borderClass: 'bg-red-500',
    dotClass: 'bg-red-500',
    iconClass: 'text-red-500',
  },
  orange: {
    color: 'orange',
    borderClass: 'bg-orange-500',
    dotClass: 'bg-orange-500',
    iconClass: 'text-orange-500',
  },
  blue: {
    color: 'blue',
    borderClass: 'bg-blue-500',
    dotClass: 'bg-blue-500',
    iconClass: 'text-blue-500',
  },
};

export function getStatusVisual(type: TInsuranceNotificationType): INotificationStatusVisual {
  switch (type) {
    case 'claim_approved':
    case 'claim_paid':
    case 'policy_renewed':
    case 'policy_approved':
    case 'preauth_approved':
      return STATUS_VISUALS.green;

    case 'claim_under_review':
    case 'claim_partially_approved':
    case 'claim_partially_paid':
    case 'preauth_under_review':
    case 'ai_status_update':
      return STATUS_VISUALS.amber;

    case 'claim_rejected':
    case 'policy_rejected':
    case 'policy_cancelled':
    case 'preauth_denied':
      return STATUS_VISUALS.red;

    case 'policy_expiring':
    case 'preauth_expiring':
    case 'claim_additional_info_required':
      return STATUS_VISUALS.orange;

    case 'claim_submitted':
    case 'claim_docs_uploaded':
    case 'policy_submitted_for_review':
    case 'preauth_requested':
    case 'preauth_submitted':
    case 'invoice_created':
    case 'receipt_uploaded':
      return STATUS_VISUALS.blue;

    default:
      return STATUS_VISUALS.blue;
  }
}

// ============================================================================
// Icon Name Resolution (for Lucide icon mapping)
// ============================================================================

export type TNotificationIconName =
  | 'circle-check'
  | 'circle-x'
  | 'clock'
  | 'triangle-alert'
  | 'info'
  | 'clipboard-list'
  | 'eye'
  | 'dollar-sign'
  | 'upload'
  | 'shield-check'
  | 'shield-x'
  | 'shield-alert'
  | 'file-text'
  | 'receipt'
  | 'alert-circle';

export function getNotificationIconName(type: TInsuranceNotificationType): TNotificationIconName {
  switch (type) {
    case 'claim_approved':
    case 'policy_approved':
    case 'policy_renewed':
      return 'circle-check';
    case 'claim_rejected':
    case 'policy_rejected':
    case 'policy_cancelled':
      return 'circle-x';
    case 'claim_under_review':
    case 'preauth_under_review':
      return 'eye';
    case 'claim_additional_info_required':
    case 'policy_expiring':
    case 'preauth_expiring':
      return 'triangle-alert';
    case 'claim_submitted':
    case 'policy_submitted_for_review':
      return 'clipboard-list';
    case 'claim_partially_approved':
    case 'ai_status_update':
      return 'alert-circle';
    case 'claim_paid':
    case 'claim_partially_paid':
      return 'dollar-sign';
    case 'claim_docs_uploaded':
      return 'upload';
    case 'preauth_requested':
    case 'preauth_submitted':
      return 'shield-alert';
    case 'preauth_approved':
      return 'shield-check';
    case 'preauth_denied':
      return 'shield-x';
    case 'invoice_created':
      return 'receipt';
    case 'receipt_uploaded':
      return 'file-text';
    default:
      return 'alert-circle';
  }
}

// ============================================================================
// Icon Component Map (shared by card components to avoid duplication)
// ============================================================================

export const NOTIFICATION_ICON_MAP: Record<
  TNotificationIconName,
  React.ComponentType<{ className?: string }>
> = {
  'circle-check': CheckCircle,
  'circle-x': XCircle,
  clock: Clock,
  'triangle-alert': AlertTriangle,
  info: Info,
  'clipboard-list': ClipboardList,
  eye: Eye,
  'dollar-sign': DollarSign,
  upload: Upload,
  'shield-check': ShieldCheck,
  'shield-x': ShieldX,
  'shield-alert': ShieldAlert,
  'file-text': FileText,
  receipt: Receipt,
  'alert-circle': AlertCircle,
};
